import { createContext, useState, ReactNode, useContext, useCallback, useEffect } from "react";
import { useBackofficeSSE } from "../hooks/useBackofficeSSE";
import {
	getNotifications,
	markNotificationRead,
	markAllNotificationsRead,
} from "../services/notification_service";
import { getTokenFromLocal } from "../helpers/appHelpers";
import { isTokenExpired } from "../helpers/authenticationHelpers";

export type NotificationKind =
	| "cash_movement_pending"
	| "cash_movement_decided"
	| "approval"
	| "approval_request"
	| "sale_return"
	| "sale_cancellation"
	| "cash_count_review"
	| "cash_shift_close"
	| "notification";

export type BackofficeNotification = {
	id: string;
	notification_id?: number;
	kind: NotificationKind;
	title?: string;
	message: string;
	branch_id: number;
	at: Date;
	read: boolean;
	payload: any;
	entity_type?: string | null;
	entity_id?: number | null;
};

interface BackofficeNotificationContextType {
	notifications: BackofficeNotification[];
	unreadCount: number;
	addNotification: (n: Omit<BackofficeNotification, "id" | "read" | "at">) => void;
	markRead: (id: string, notification_id?: number) => void;
	markAllRead: () => void;
}

const BackofficeNotificationContext = createContext<BackofficeNotificationContextType>({
	notifications: [],
	unreadCount: 0,
	addNotification: () => {},
	markRead: () => {},
	markAllRead: () => {},
});

export const BackofficeNotificationProvider = ({ children }: { children: ReactNode }) => {
	const [notifications, setNotifications] = useState<BackofficeNotification[]>([]);

	// Load persisted notifications from API on mount (only when authenticated)
	useEffect(() => {
		const token = getTokenFromLocal();
		if (!token || isTokenExpired()) return;

		getNotifications({ per_page: 50 }).then((res: any) => {
			if (!res || !res.success) return;
			const loaded: BackofficeNotification[] = (res.data?.items ?? []).map((item: any) => ({
				id: String(item.notification_id),
				notification_id: item.notification_id,
				kind: item.kind as NotificationKind,
				title: item.title,
				message: item.body || item.title,
				branch_id: item.branch_id ?? 0,
				at: new Date(item.created_date),
				read: item.is_read,
				payload: item,
				entity_type: item.entity_type,
				entity_id: item.entity_id,
			}));
			setNotifications(loaded);
		});
	}, []);

	const addNotification = useCallback(
		(n: Omit<BackofficeNotification, "id" | "read" | "at">) => {
			const newNotif: BackofficeNotification = {
				...n,
				id: n.notification_id ? String(n.notification_id) : `${Date.now()}-${Math.random()}`,
				at: new Date(),
				read: false,
			};
			setNotifications((prev) => {
				// Deduplicate by notification_id
				if (n.notification_id) {
					const exists = prev.some((p) => p.notification_id === n.notification_id);
					if (exists) return prev;
				}
				return [newNotif, ...prev].slice(0, 50);
			});
		},
		[],
	);

	const markRead = useCallback((id: string, notification_id?: number) => {
		setNotifications((prev) =>
			prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
		);
		if (notification_id != null) {
			markNotificationRead(notification_id).catch(() => {});
		}
	}, []);

	const markAllRead = useCallback(() => {
		setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
		markAllNotificationsRead().catch(() => {});
	}, []);

	useBackofficeSSE(addNotification);

	const unreadCount = notifications.filter((n) => !n.read).length;

	return (
		<BackofficeNotificationContext.Provider
			value={{ notifications, unreadCount, addNotification, markRead, markAllRead }}
		>
			{children}
		</BackofficeNotificationContext.Provider>
	);
};

export const useBackofficeNotification = () =>
	useContext(BackofficeNotificationContext);
